// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { TerceraChartCashItemSeriesDataType } from '../Series/TerceraChartCashItemSeriesEnums';
import { Pen, SolidBrush, PenStyle } from '@shared/commons/Graphics';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { Periods } from '@shared/utils/History/TFInfo';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { ChartZoomRendererButtons } from './TerceraChartZoomRenderer';
import { BarIndex } from '@shared/commons/cache/History/HistoryConstants';
import { LayersEnum } from '../Utils/LayersEnum';
export class TerceraChartCrossHairRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.ShowTrackVisibleLine = true;
        this.gridPricePen = new Pen('blue');
        this.gridPriceHLPen = new Pen('blue');
        this.gridTimePen = new Pen('blue');
        this.crossHairWidth = 1;
        this.chart = chart;
        this.assignLayer = LayersEnum.CrossHair;
        this.SetClassName('TerceraChartCrossHairRenderer');
    }
    get CrossHairColor() {
        return this.crossHairColor;
    }
    set CrossHairColor(value) {
        this.crossHairColor = value;
        this.UpdatePricePen();
    }
    get CrossHairStyle() {
        return this.crossHairStyle;
    }
    set CrossHairStyle(value) {
        this.crossHairStyle = value;
        this.UpdatePricePen();
    }
    get CrossHairWidth() {
        return this.crossHairWidth;
    }
    set CrossHairWidth(value) {
        this.crossHairWidth = value;
        this.UpdatePricePen();
    }
    get TrackPriceColor() {
        return this.trackPriceColor;
    }
    set TrackPriceColor(value) {
        this.trackPriceColor = value;
        this.TrackCursorBackColorBrush = new SolidBrush(this.trackPriceColor);
        this.trackPriceBoredrPen = new Pen(this.trackPriceColor, 1, PenStyle.SimpleChart);
    }
    Draw(gr, window, windowsContainer, advParams = null, isMobile = false) {
        var _a;
        const p = advParams;
        if (!p)
            return;
        let pHoverWindowXValue = p.HoverWindowXValue;
        const terceraChart = p.TerceraChart;
        const cursorPosition = p.CursorPosition;
        this.lastMouseLoc = cursorPosition;
        window = terceraChart.HoverWindow;
        const chartCursorPosition = terceraChart.CursorPosition;
        const timeScaleRenderer = terceraChart.windowsContainer.xScaleRenderer;
        const zoomRenderer = terceraChart.windowsContainer.zoomRenderer;
        const windows = terceraChart.windowsContainer.Windows;
        if (chartCursorPosition &&
            !window &&
            timeScaleRenderer.Rectangle.Contains(chartCursorPosition.X, chartCursorPosition.Y)) {
            window = windows[windows.length - 1];
            pHoverWindowXValue = window.PointsConverter.GetDataX(chartCursorPosition.X);
        }
        if (!window || !this.Visible)
            return;
        if (!isMobile) {
            if (!terceraChart.HasCorrectData() ||
                !p.Instrument ||
                pHoverWindowXValue <= 0 ||
                !terceraChart.HasCorrectData() ||
                this.TradingToolHovered(p.TerceraChart) ||
                zoomRenderer.CurrentHoverButtonType !== ChartZoomRendererButtons.None) {
                return;
            }
        }
        const instrument = p.Instrument;
        const clientRect = window.ClientRectangle;
        if (!cursorPosition || cursorPosition.IsEmpty()) {
            return;
        }
        this.DrawHLine(gr, clientRect, cursorPosition.Y);
        const topDelta = window === terceraChart.mainWindow
            ? ((_a = window.GetMainThreadRenderer('TerceraChartPlateRenderer')) === null || _a === void 0 ? void 0 : _a.IsEmpty)
                ? window.rightYScaleRenderer.AutoScaleRect.Height
                : 0
            : 0;
        const formattedValue = TerceraChartCrossHairRenderer.GetPriceText(window, p, instrument);
        if (window.rightYScaleRenderer &&
            !window.rightYScaleRenderer.AutoScaleRect.Contains(cursorPosition.X, cursorPosition.Y) &&
            !window.rightYScaleRenderer.ScaleButtonRect.Contains(cursorPosition.X, cursorPosition.Y)) {
            this.DrawPricePlate(gr, window, clientRect, cursorPosition, topDelta, formattedValue);
        }
        this.DrawVLine(gr, clientRect, cursorPosition.X);
        if (!window)
            return;
        const barIndex = Math.floor(window.PointsConverter.GetDataIndexByX(cursorPosition.X));
        const time = DateTimeConvertor.getLocalTimeQuick(terceraChart.MainCashItemSeries.GetValue(barIndex, BarIndex.Time));
        const timeFormatted = time.getFullYear() > DateTimeUtils.StartDateTimeJavascriptYear
            ? Periods.PeriodsFormatWithConvert(time, terceraChart.TimeFrameInfo().Periods, instrument)
            : '-';
        this.DrawTimePlate(gr, clientRect, cursorPosition.X, timeFormatted);
    }
    ProcessMouseDown(e) {
        if (e.Button === MouseButtons.Middle) {
            this.ShowTrackVisibleLine = !this.ShowTrackVisibleLine;
        }
        return super.ProcessMouseDown(e);
    }
    UpdatePricePen() {
        this.crossHairPen = new Pen(this.crossHairColor, this.crossHairWidth, this.crossHairStyle);
    }
    DrawHLine(gr, clientRect, y) {
        const clientRectY = clientRect.Y;
        if (this.ShowTrackVisibleLine && y > clientRectY && y < clientRectY + clientRect.Height) {
            gr.DrawLine(this.crossHairPen, clientRect.X, y, clientRect.X + clientRect.Width, y);
        }
    }
    DrawVLine(gr, clientRect, x) {
        const crX = clientRect.X;
        if (this.ShowTrackVisibleLine && x > crX && x < crX + clientRect.Width) {
            gr.DrawLine(this.crossHairPen, x, clientRect.Y, x, clientRect.Y + clientRect.Height);
        }
    }
    DrawTimePlate(gr, clientRect, x, timeFormated) {
        const ScaleFont = this.chart.yScaleRendererSettings.ScaleFont;
        const timseScaleRect = this.chart.XScaleRect();
        let textWidth = gr.GetTextWidth(timeFormated, ScaleFont);
        textWidth += 5;
        const LastTimeRect = new Rectangle(x - textWidth / 2, timseScaleRect.Y, textWidth, timseScaleRect.Height);
        LastTimeRect.Inflate(0, -1);
        const clientRectX = clientRect.X;
        const LastTimeRectX = LastTimeRect.X;
        if (LastTimeRectX > clientRectX && LastTimeRectX + LastTimeRect.Width < clientRectX + clientRect.Width) {
            gr.RoundRectangle(LastTimeRect, 4, this.TrackCursorBackColorBrush, this.trackPriceBoredrPen);
            LastTimeRect.Y++;
            gr.DrawString(timeFormated, ScaleFont, this.TrackCursorFontBrush, LastTimeRect.X + LastTimeRect.Width / 2, LastTimeRect.Y + LastTimeRect.Height / 2, 'center', 'middle');
        }
    }
    DrawPricePlate(gr, window, clientRect, cursorPosition, topDelta, formattedValue) {
        const lastPriceRectHeight = 22;
        const lastPriceRect = new Rectangle(clientRect.X + clientRect.Width, cursorPosition.Y - lastPriceRectHeight / 2, window.PaddingRight - 1, // Minus 1 so that it doesn't stick too closely to the edge of the canvas
        lastPriceRectHeight);
        if (lastPriceRect.Y > clientRect.Y + topDelta &&
            lastPriceRect.Y + lastPriceRectHeight < clientRect.Y + clientRect.Height) {
            gr.RoundRectangle(lastPriceRect, 4, this.TrackCursorBackColorBrush);
            lastPriceRect.Y += 1;
            const ScaleFont = this.chart.yScaleRendererSettings.ScaleFont;
            gr.DrawString(formattedValue, ScaleFont, this.TrackCursorFontBrush, lastPriceRect.X + lastPriceRect.Width / 2, lastPriceRect.Y + lastPriceRectHeight / 2, 'center', 'middle');
        }
    }
    Dispose() {
        super.Dispose();
        this.chart = null;
    }
    ThemeChanged() {
        super.ThemeChanged();
        this.TrackPriceColor = ThemeManager.colors.Chart_TrackCursorBackColor;
        this.TrackCursorFontBrush = new SolidBrush(ThemeManager.colors.Chart_TrackCursorFontColor);
        this.CrossHairColor = ThemeManager.colors.chartCrossHair;
        this.CrossHairStyle = ThemeManager.styles.crosshairStyle;
    }
    static GetPriceText(window, p, instrument) {
        const cashItemSeries = p.mainPriceRenderer.Series;
        let formatcurPriceValue = '';
        const dataType = window === p.TerceraChart.mainWindow
            ? cashItemSeries.settings.DataType
            : TerceraChartCashItemSeriesDataType.Absolute;
        formatcurPriceValue = cashItemSeries.settings.FormatPriceConverter(p.HoverWindowYValue, instrument, dataType);
        return formatcurPriceValue;
    }
    TradingToolHovered(chart) {
        if ((chart === null || chart === void 0 ? void 0 : chart.TerceraChartTradingToolsRenderer) != null) {
            const curTool = chart.TerceraChartTradingToolsRenderer.hoverTool;
            if (curTool && !curTool.newTrade) {
                return true;
            }
        }
        return false;
    }
    ProcessMouseMove(e) {
        if (this.Visible &&
            (isNullOrUndefined(this.lastMouseLoc) ||
                e.Location.X !== this.lastMouseLoc.X ||
                e.Location.Y !== this.lastMouseLoc.Y)) {
            e.NeedRedraw |= LayersEnum.CrossHair;
        }
        return false;
    }
}
